// ============================================================================
//  HR Module — Paie
//  PayrollPage · PayslipDetailPage
// ============================================================================

import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  DollarSign, Play, CheckCircle, Lock, ChevronLeft, Download,
  Users, TrendingUp, Clock, AlertTriangle, RefreshCw, Shield,
  FileText, ChevronRight, BarChart3, ArrowRight, Unlock, Printer,
  Plus, Trash2, X, Calculator, FileCheck, Hash,
} from 'lucide-react'
import {
  hrApi,
  type PayrollPeriod, type Payslip,
  type PayrollVariable, type PayrollVariableType, type CreatePayrollVariableDto,
  type GrossUpDto, type GrossUpResult,
} from '@/modules/hr/api/hr.api'
import { useNotificationStore } from '@/stores/notification.store'
import { fmtXOF, toNum } from '@/lib/decimal'

const fmt    = (n: number) => new Intl.NumberFormat('fr-SN', { minimumFractionDigits: 0 }).format(Math.round(n))
// fmtXOF → voir @/lib/decimal

const PERIOD_STATUS: Record<string, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  OPEN:       { label: 'Ouvert',     color: 'text-[#059669]', bg: 'bg-[#10B981]/10 border-[rgba(16,185,129,0.25)]', icon: Unlock },
  CALCULATED: { label: 'Calculé',   color: 'text-[var(--blue-d)]',    bg: 'bg-[#E0F2FE] border-[rgba(245,124,0,0.25)]',       icon: BarChart3 },
  VALIDATED:  { label: 'Validé',    color: 'text-[#7C3AED]',  bg: 'bg-[#EDE9FE] border-[rgba(124,58,237,0.25)]',   icon: CheckCircle },
  APPROVED:   { label: 'Approuvé',  color: 'text-[#D97706]',   bg: 'bg-[#FEF3C7] border-[rgba(245,158,11,0.25)]',     icon: Shield },
  PAID:       { label: 'Payé',      color: 'text-[#0F766E]',    bg: 'bg-[#F0FDFA] border-[rgba(20,184,166,0.25)]',       icon: DollarSign },
  LOCKED:     { label: 'Clôturé',   color: 'text-[var(--tx3)]',   bg: 'bg-[#D6D2EC]/10 border-[var(--tx3)]/20',     icon: Lock },
}

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre']

// ============================================================================
//  PAYROLL PAGE — Liste des périodes
// ============================================================================

export default function PayrollPage() {
  const [year, setYear]       = useState(new Date().getFullYear())
  const [showNew, setShowNew] = useState(false)
  const [newYear, setNewYear] = useState(new Date().getFullYear())
  const [newMonth, setNewMonth] = useState(new Date().getMonth() + 1)
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore(s => s.addNotification)

  const { data, isLoading } = useQuery({
    queryKey: ['payrollPeriods', year],
    queryFn:  () => hrApi.payroll.listPeriods({ year }),
  })

  const { mutate: openPeriod, isPending: isOpening } = useMutation({
    mutationFn: () => hrApi.payroll.openPeriod({ year: newYear, month: newMonth }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payrollPeriods'] })
      setShowNew(false)
      addNotification({ type: 'success', title: 'Période ouverte', message: `Paie ${MONTHS_FR[newMonth - 1]} ${newYear} créée.` })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const periods = data?.periods ?? []

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Paie</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Gestion des périodes de paie</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/hr/payroll/declarations"
            className="flex items-center gap-2 px-3 py-2 bg-white border border-[var(--bd)] hover:bg-[var(--surface-2)] text-sm font-medium text-[var(--tx2)] rounded-lg transition-colors"
          >
            <FileText className="w-4 h-4" />
            Déclarations
          </Link>
          <select value={year} onChange={e => setYear(+e.target.value)}
            className="px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => setShowNew(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors">
            <Play className="w-4 h-4" />
            Ouvrir une période
          </button>
        </div>
      </div>

      {/* Modal nouvelle période */}
      {showNew && (
        <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white border border-[var(--bd)] rounded-2xl p-6 w-full max-w-lg shadow-2xl">
            <h3 className="text-lg font-bold text-[var(--brand-dark)] mb-4">Ouvrir une période de paie</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-[var(--tx3)] mb-1.5">Année</label>
                <input type="number" value={newYear} onChange={e => setNewYear(+e.target.value)}
                  className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />
              </div>
              <div>
                <label className="block text-xs text-[var(--tx3)] mb-1.5">Mois</label>
                <select value={newMonth} onChange={e => setNewMonth(+e.target.value)}
                  className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]">
                  {MONTHS_FR.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowNew(false)}
                className="flex-1 py-2 text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)] border border-[var(--bd)] rounded-lg transition-colors">
                Annuler
              </button>
              <button onClick={() => openPeriod()} disabled={isOpening}
                className="flex-1 py-2 text-sm font-medium bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg transition-colors disabled:opacity-50">
                {isOpening ? 'Création…' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grille des périodes */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          <div className="w-8 h-8 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : periods.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 bg-[var(--bg)] border border-[var(--bd)] rounded-xl text-[var(--tx3)]">
          <DollarSign className="w-8 h-8 mb-2 opacity-50" />
          <p className="text-sm">Aucune période pour {year}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {periods.map(period => {
            const cfg = PERIOD_STATUS[period.status]
            const Icon = cfg.icon
            return (
              <Link key={period.id} to={`/hr/payroll/${period.id}`}
                className="bg-[var(--bg)] border border-[var(--bd)] hover:border-blue-500/30 rounded-xl p-5 transition-all group">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-lg font-bold text-[var(--brand-dark)]">{MONTHS_FR[period.month - 1]} {period.year}</p>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border mt-1 ${cfg.bg} ${cfg.color}`}>
                      <Icon className="w-3 h-3" />
                      {cfg.label}
                    </span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[var(--tx3)] group-hover:text-[var(--blue-d)] transition-colors mt-1" />
                </div>

                {period.stats && (
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between text-[var(--tx3)]">
                      <span>Masse salariale brute</span>
                      <span className="font-medium text-[var(--brand-dark)]">{fmtXOF(period.stats.totalGross)}</span>
                    </div>
                    <div className="flex justify-between text-[var(--tx3)]">
                      <span>Net à payer</span>
                      <span className="font-medium text-[#059669]">{fmtXOF(period.stats.totalNet)}</span>
                    </div>
                    <div className="flex justify-between text-[var(--tx3)]">
                      <span>Charges patronales</span>
                      <span className="font-medium text-[#D97706]">{fmtXOF(period.stats.totalEmployer)}</span>
                    </div>
                    <div className="pt-2 border-t border-[var(--bd)] flex justify-between text-[var(--tx2)]">
                      <span>Coût total entreprise</span>
                      <span className="font-bold text-[var(--brand-dark)]">{fmtXOF(period.stats.coutTotal)}</span>
                    </div>
                    <p className="text-[var(--tx3)]">{period.stats.effectif} salarié{period.stats.effectif > 1 ? 's' : ''}</p>
                  </div>
                )}
                {period._count && !period.stats && (
                  <p className="text-xs text-[var(--tx3)]">{period._count.payslips} bulletin{period._count.payslips > 1 ? 's' : ''}</p>
                )}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  PAYROLL DETAIL PAGE — Gestion d'une période
// ============================================================================


// ─── Export CSV masse salariale ───────────────────────────────────────────
function exportPayrollCsv(period: any) {
  const payslips = period.payslips ?? []
  const monthNames = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']
  const periodLabel = monthNames[(period.month as number) - 1] + ' ' + period.year

  const headers = ['Matricule','Nom','Prénom','Poste','Salaire brut','Retenues','Net à payer','Statut']
  const rows = payslips.map((p: any) => [
    p.employee?.matricule ?? '',
    p.employee?.lastName ?? '',
    p.employee?.firstName ?? '',
    p.employee?.jobTitle ?? '',
    Number(p.grossSalary ?? 0),
    Number(p.totalDeductions ?? 0),
    Number(p.netPayable ?? 0),
    p.status ?? '',
  ])

  // Ligne totaux
  const totalGross = payslips.reduce((s: number, p: any) => s + Number(p.grossSalary ?? 0), 0)
  const totalDed   = payslips.reduce((s: number, p: any) => s + Number(p.totalDeductions ?? 0), 0)
  const totalNet   = payslips.reduce((s: number, p: any) => s + Number(p.netPayable ?? 0), 0)
  rows.push(['', '', '', 'TOTAL', totalGross, totalDed, totalNet, ''])

  const csv = [headers, ...rows].map(row => row.join(';')).join('\n')
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url
  a.download = 'Paie-' + periodLabel + '.csv'
  a.click()
  URL.revokeObjectURL(url)
}

export function PayrollDetailPage() {
  const { id }          = useParams<{ id: string }>()
  const navigate        = useNavigate()
  const queryClient     = useQueryClient()
  const addNotification = useNotificationStore(s => s.addNotification)
  const [lockReason, setLockReason]   = useState('')
  const [showLock, setShowLock]       = useState(false)
  const [showPaid, setShowPaid]       = useState(false)
  const [paymentDate,   setPaymentDate]   = useState(new Date().toISOString().split('T')[0])
  const [paymentRef,    setPaymentRef]    = useState('')
  const [paymentMethod, setPaymentMethod] = useState<'BANK_TRANSFER' | 'CHECK' | 'CASH'>('BANK_TRANSFER')

  const { data: period, isLoading } = useQuery({
    queryKey: ['payrollPeriod', id],
    queryFn:  () => hrApi.payroll.getPeriod(id!),
    enabled:  !!id,
    refetchInterval: 5000, // Poll pour le statut du calcul
  })

  const { data: calcStatus } = useQuery({
    queryKey: ['payrollStatus', id],
    queryFn:  () => hrApi.payroll.getStatus(id!),
    enabled:  period?.status === 'CALCULATED' || period?.status === 'OPEN',
    refetchInterval: 3000,
  })

  const { mutate: markPaid, isPending: isPaid } = useMutation({
    mutationFn: () => hrApi.payroll.markPaid(id!, { paymentDate, paymentRef, paymentMethod }),
    onSuccess: () => {
      setShowPaid(false)
      queryClient.invalidateQueries({ queryKey: ['payrollPeriod', id] })
      addNotification({ type: 'success', title: 'Période marquée comme payée', message: `Virement confirmé le ${paymentDate}` })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message ?? 'Erreur' }),
  })

  const mutOpts = (action: string, successMsg: string) => ({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payrollPeriod', id] })
      addNotification({ type: 'success', title: action, message: successMsg })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const { mutate: initialize, isPending: isInit }     = useMutation({ mutationFn: () => hrApi.payroll.initializePayslips(id!), ...mutOpts('Initialisation', 'Bulletins initialisés.') })
  const { mutate: calculate,  isPending: isCalc }     = useMutation({ mutationFn: () => hrApi.payroll.calculate(id!), ...mutOpts('Calcul lancé', 'Calcul IRPP/TRIMF/IPRES en cours…') })
  const { mutate: validate,   isPending: isValidate } = useMutation({ mutationFn: () => hrApi.payroll.validate(id!), ...mutOpts('Validé', 'Période validée.') })
  const { mutate: approve,    isPending: isApprove }  = useMutation({ mutationFn: () => hrApi.payroll.approve(id!), ...mutOpts('Approuvé', 'Masse salariale approuvée.') })
  const { mutate: lock,       isPending: isLock }     = useMutation({
    mutationFn: () => hrApi.payroll.lock(id!, { reason: lockReason }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['payrollPeriod', id] }); setShowLock(false); addNotification({ type: 'success', title: 'Clôture', message: 'Hard Close Social effectué.' }) },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  // [Phase 5] Génération PDF en masse pour la période
  const { mutate: generateAllPdf, isPending: isGenAllPdf } = useMutation({
    mutationFn: () => hrApi.payroll.generatePdfBatch(id!),
    onSuccess: (data) => {
      addNotification({
        type: 'success', title: 'PDF en cours',
        message: `Job ${data.jobId} en file. Voir BullBoard pour suivre l'avancement.`,
      })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message ?? 'Erreur' }),
  })

  // [Phase 5] Scellage WORM en masse pour la période
  const { mutate: sealAllPdf, isPending: isSealing } = useMutation({
    mutationFn: () => hrApi.payroll.sealPeriod(id!),
    onSuccess: (data) => {
      addNotification({
        type: 'success', title: 'Scellage WORM effectué',
        message: `${data.sealed} scellés • ${data.skipped} déjà scellés • ${data.errors} erreurs`,
      })
      queryClient.invalidateQueries({ queryKey: ['payrollPeriod', id] })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur scellage', message: err.response?.data?.message ?? 'Erreur' }),
  })

  if (isLoading) return <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" /></div>
  if (!period) return null

  const cfg    = PERIOD_STATUS[period.status]
  const Icon   = cfg.icon
  const isLocked = period.status === 'LOCKED'

  // Workflow steps
  const STEPS = ['OPEN', 'CALCULATED', 'VALIDATED', 'APPROVED', 'PAID', 'LOCKED']
  const stepIdx = STEPS.indexOf(period.status)

  return (
    <div className="space-y-5 nx-fade-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
            <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[var(--brand-dark)]">{MONTHS_FR[period.month - 1]} {period.year}</h1>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border mt-1 ${cfg.bg} ${cfg.color}`}>
              <Icon className="w-3 h-3" />
              {cfg.label}
            </span>
          </div>
        </div>

        {/* Actions workflow */}
        <div className="flex items-center gap-2">
          {period.status === 'OPEN' && (
            <>
              <button onClick={() => initialize()} disabled={isInit}
                className="flex items-center gap-2 px-3 py-2 bg-[var(--surface-2)] hover:bg-[var(--bd)] text-sm text-[var(--brand-dark)] rounded-lg transition-colors disabled:opacity-50">
                <Users className="w-4 h-4" />
                {isInit ? 'Initialisation…' : 'Initialiser bulletins'}
              </button>
              {calcStatus?.total > 0 && (
                <button onClick={() => calculate()} disabled={isCalc}
                  className="flex items-center gap-2 px-3 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-sm text-white rounded-lg transition-colors disabled:opacity-50">
                  <Play className="w-4 h-4" />
                  {isCalc ? 'Lancement…' : 'Calculer'}
                </button>
              )}
            </>
          )}
          {period.status === 'CALCULATED' && calcStatus?.draft === 0 && (
            <button onClick={() => validate()} disabled={isValidate}
              className="flex items-center gap-2 px-3 py-2 bg-violet-600 hover:bg-violet-500 text-sm text-[var(--brand-dark)] rounded-lg transition-colors disabled:opacity-50">
              <CheckCircle className="w-4 h-4" />
              {isValidate ? 'Validation…' : 'Valider (RH)'}
            </button>
          )}
          {period.status === 'VALIDATED' && (
            <button onClick={() => approve()} disabled={isApprove}
              className="flex items-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-500 text-sm text-[var(--brand-dark)] rounded-lg transition-colors disabled:opacity-50">
              <Shield className="w-4 h-4" />
              {isApprove ? 'Approbation…' : 'Approuver (DAF)'}
            </button>
          )}
          {period.status === 'APPROVED' && (
            <button onClick={() => setShowPaid(true)}
              className="flex items-center gap-2 px-3 py-2 bg-[#0F766E] hover:bg-teal-600 text-white text-sm rounded-lg transition-colors">
              <DollarSign className="w-4 h-4" />
              Marquer comme payé
            </button>
          )}
          {period.status === 'PAID' && (
            <button onClick={() => setShowLock(true)}
              className="flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-500 text-sm text-[var(--brand-dark)] rounded-lg transition-colors">
              <Lock className="w-4 h-4" />
              Hard Close Social
            </button>
          )}

          {/* [Phase 5] Actions batch PDF / Seal — disponibles dès CALCULATED */}
          {(period.status === 'CALCULATED' || period.status === 'VALIDATED' || period.status === 'APPROVED' || period.status === 'PAID') && (
            <>
              <div className="h-6 w-px bg-[var(--bd)] mx-1" />
              <button
                onClick={() => generateAllPdf()}
                disabled={isGenAllPdf}
                className="flex items-center gap-2 px-3 py-2 bg-[var(--brand-amber)] hover:bg-[#4F3573] text-sm text-white rounded-lg transition-colors disabled:opacity-50"
                title="Génère le PDF de tous les bulletins (asynchrone)"
              >
                <FileText className="w-4 h-4" />
                {isGenAllPdf ? 'Génération…' : 'Tous les PDF'}
              </button>
              <button
                onClick={() => {
                  if (confirm('Sceller tous les bulletins de cette période dans l\'archive WORM (10 ans) ? Cette action est irréversible.')) {
                    sealAllPdf()
                  }
                }}
                disabled={isSealing}
                className="flex items-center gap-2 px-3 py-2 bg-[var(--sb-bg)] hover:bg-[var(--brand-dark)] text-sm text-white rounded-lg transition-colors disabled:opacity-50"
                title="Scellage WORM legal-archive de toute la période"
              >
                <Lock className="w-4 h-4" />
                {isSealing ? 'Scellage…' : 'Sceller période'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Workflow progress */}
      <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between">
          {STEPS.map((step, i) => {
            const done    = i < stepIdx
            const current = i === stepIdx
            const labels  = ['Ouvert', 'Calculé', 'Validé', 'Approuvé', 'Payé', 'Clôturé']
            return (
              <div key={step} className="flex items-center flex-1">
                <div className={`flex flex-col items-center ${i < STEPS.length - 1 ? 'flex-1' : ''}`}>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    done ? 'border-emerald-500 bg-[#10B981]' :
                    current ? 'border-blue-500 bg-[#BAE6FD]' :
                    'border-[#D6D2EC]'
                  }`}>
                    {done && <CheckCircle className="w-3.5 h-3.5 text-[var(--brand-dark)]" />}
                    {current && <span className="w-2 h-2 rounded-full bg-[#38BDF8]" />}
                  </div>
                  <span className={`text-xs mt-1 ${current ? 'text-[var(--blue-d)] font-medium' : done ? 'text-[#059669]' : 'text-[var(--tx3)]'}`}>
                    {labels[i]}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-px mx-1 ${i < stepIdx ? 'bg-[#10B981]' : 'bg-[var(--surface-2)]'}`} />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* KPIs masse salariale */}
      {period.stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 nx-stagger">
          {[
            { label: 'Brut total',      value: fmtXOF(period.stats.totalGross),    color: 'bg-[#E0F2FE] text-[var(--blue-d)]',    icon: DollarSign },
            { label: 'Net à payer',     value: fmtXOF(period.stats.totalNet),      color: 'bg-[#10B981]/10 text-[#059669]', icon: TrendingUp },
            { label: 'Charges patron.', value: fmtXOF(period.stats.totalEmployer), color: 'bg-[#FEF3C7] text-[#D97706]',  icon: BarChart3 },
            { label: 'Coût total',      value: fmtXOF(period.stats.coutTotal),     color: 'bg-[#EDE9FE] text-[#7C3AED]', icon: Users },
          ].map(k => (
            <div key={k.label} className="bg-white border border-[var(--bd)] rounded-xl p-5 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow" style={{borderTop:`3px solid ${k.strip??k.color?.match(/#[0-9A-Fa-f]+/)?.[0]??'var(--brand-orange)'}`}}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${k.color}`}>
                <k.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-lg font-bold text-[var(--brand-dark)]">{k.value}</p>
                <p className="text-xs text-[var(--tx3)]">{k.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Statut calcul en cours */}
      {calcStatus && !isLocked && period.status !== 'LOCKED' && (
        <div className="bg-white border border-[var(--bd)] rounded-xl p-5 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${calcStatus.draft > 0 ? 'bg-[#F59E0B] animate-pulse' : 'bg-[#10B981]'}`} />
            <p className="text-sm text-[var(--tx2)]">
              {calcStatus.draft > 0
                ? `Calcul en cours — ${calcStatus.calculated}/${calcStatus.total} bulletins calculés`
                : `${calcStatus.total} bulletins calculés`}
            </p>
          </div>
          {calcStatus.draft > 0 && (
            <button onClick={() => queryClient.invalidateQueries({ queryKey: ['payrollStatus', id] })}
              className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
              <RefreshCw className="w-4 h-4 text-[var(--tx3)]" />
            </button>
          )}
        </div>
      )}

      {/* Table des bulletins */}
      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--bd)] flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--brand-dark)]">Bulletins de paie ({period.payslips?.length ?? 0})</h3>
          {!isLocked && (
            <button
              onClick={() => exportPayrollCsv(period)}
              className="flex items-center gap-2 text-xs text-[var(--tx3)] hover:text-[var(--tx)] transition-colors">
              <Download className="w-3 h-3" />
              Export CSV
            </button>
          )}
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--bd)]">
              {['Employé', 'Poste', 'Salaire brut', 'Cotisations', 'Net à payer', 'Statut', ''].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[var(--tx3)] uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--bd)]">
            {(period.payslips ?? []).map(p => (
              <tr key={p.id} className="hover:bg-[var(--surface-2)] transition-colors group">
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-[var(--brand-dark)]">{p.employee?.firstName} {p.employee?.lastName}</p>
                    <p className="text-xs text-[var(--tx3)]">{p.employee?.matricule}</p>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-[var(--tx2)]">{p.employee?.jobTitle}</td>
                <td className="px-4 py-3 text-sm text-[var(--brand-dark)] font-medium">{fmtXOF(p.grossSalary)}</td>
                <td className="px-4 py-3 text-sm text-[#DC2626]">{toNum(p.totalDeductions) > 0 ? `−${fmtXOF(p.totalDeductions)}` : fmtXOF(p.totalDeductions)}</td>
                <td className="px-4 py-3 text-sm text-[#059669] font-bold">{fmtXOF(p.netPayable)}</td>
                <td className="px-4 py-3">
                  <span className="text-xs text-[var(--tx3)] capitalize">{p.status}</span>
                </td>
                <td className="px-4 py-3">
                  <Link to={`/hr/payroll/payslips/${p.id}`}
                    className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-xs text-[var(--blue-d)] transition-opacity">
                    Détail <ArrowRight className="w-3 h-3" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Hard Close */}
      {showPaid && (
        <div className="fixed inset-0 bg-[rgba(13,8,32,0.4)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
          <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-md p-6 space-y-4"
            style={{ boxShadow:'0 24px 64px rgba(13,8,32,0.18)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#F0FDFA] rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#0F766E]" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[var(--brand-dark)]">Confirmer le paiement</h3>
                <p className="text-xs text-[var(--tx3)]">Janvier {period?.year} — {fmtXOF(period?.stats?.totalNet)} net à payer</p>
              </div>
            </div>
            <div className="space-y-3">
              {/* Mode de paiement */}
              <div>
                <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Mode de paiement *</label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { value: 'BANK_TRANSFER', label: '🏦 Virement', desc: 'Bancaire' },
                    { value: 'CHECK',         label: '📄 Chèque',   desc: 'Chèque' },
                    { value: 'CASH',          label: '💵 Espèces',  desc: 'Cash' },
                  ] as const).map(opt => (
                    <button key={opt.value} onClick={() => setPaymentMethod(opt.value)}
                      className={`px-3 py-2 rounded-lg border text-xs font-medium transition-colors text-center ${
                        paymentMethod === opt.value
                          ? 'bg-[var(--blue)] border-[var(--blue)] text-white'
                          : 'bg-[var(--surface-2)] border-[var(--bd)] text-[var(--tx3)] hover:border-[var(--blue)]'
                      }`}>
                      <div>{opt.label}</div>
                    </button>
                  ))}
                </div>
              </div>
              {/* Date */}
              <div>
                <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
                  Date de {paymentMethod === 'BANK_TRANSFER' ? 'virement' : paymentMethod === 'CHECK' ? 'chèque' : 'paiement'} *
                </label>
                <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)}
                  className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:border-[var(--blue)]" />
              </div>
              {/* Référence */}
              <div>
                <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
                  {paymentMethod === 'BANK_TRANSFER' ? 'Référence virement' : paymentMethod === 'CHECK' ? 'N° chèque' : 'Référence (optionnel)'}
                </label>
                <input type="text" value={paymentRef} onChange={e => setPaymentRef(e.target.value)}
                  className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:border-[var(--blue)]"
                  placeholder={paymentMethod === 'BANK_TRANSFER' ? 'ex : VIR-2026-001' : paymentMethod === 'CHECK' ? 'ex : CHQ-00123' : 'ex : Caisse centrale'} />
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setShowPaid(false)}
                className="flex-1 px-4 py-2 border border-[var(--bd)] rounded-xl text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors">
                Annuler
              </button>
              <button onClick={() => markPaid()} disabled={isPaid || !paymentDate}
                className="flex-1 px-4 py-2 bg-[#0F766E] text-white rounded-xl text-sm font-semibold hover:bg-teal-600 transition-colors disabled:opacity-50">
                {isPaid ? '…' : 'Confirmer le paiement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLock && (
        <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white border border-[rgba(239,68,68,0.35)] rounded-2xl p-6 w-full max-w-2xl shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#DC2626]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[var(--brand-dark)]">Hard Close Social</h3>
                <p className="text-xs text-[#DC2626]">Action irréversible</p>
              </div>
            </div>
            <p className="text-sm text-[var(--tx3)] mb-4">
              Cette action verrouille définitivement la période, scelle les bulletins PDF (WORM 10 ans) et génère les écritures comptables.
            </p>
            <div>
              <label className="block text-xs text-[var(--tx3)] mb-1.5">Motif (obligatoire)</label>
              <textarea
                value={lockReason}
                onChange={e => setLockReason(e.target.value)}
                placeholder="Motif de la clôture sociale…"
                rows={3}
                className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-red-500 resize-none"
              />
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowLock(false)}
                className="flex-1 py-2 text-sm text-[var(--tx3)] border border-[var(--bd)] rounded-lg hover:text-[var(--brand-dark)] transition-colors">
                Annuler
              </button>
              <button onClick={() => lock()} disabled={lockReason.length < 10 || isLock}
                className="flex-1 py-2 text-sm font-medium bg-red-600 hover:bg-red-500 text-[var(--brand-dark)] rounded-lg transition-colors disabled:opacity-50">
                {isLock ? 'Clôture…' : 'Confirmer la clôture'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  PAYSLIP DETAIL PAGE — Bulletin individuel
// ============================================================================


export function PayslipDetailPage() {
  const { id }      = useParams<{ id: string }>()
  const navigate    = useNavigate()
  const qc          = useQueryClient()
  const notify      = useNotificationStore(s => s.addNotification)

  const [showVarForm,  setShowVarForm]  = useState(false)
  const [showGrossUp,  setShowGrossUp]  = useState(false)
  const [pdfUrl,       setPdfUrl]       = useState<string | null>(null)

  // ─── Queries ──────────────────────────────────────────────────────────────
  const { data: payslip, isLoading } = useQuery({
    queryKey: ['payslip', id],
    queryFn:  () => hrApi.payroll.getPayslip(id!),
    enabled:  !!id,
  })

  const { data: variables = [] } = useQuery({
    queryKey: ['payslip-variables', id],
    queryFn:  () => hrApi.payroll.listVariables(id!),
    enabled:  !!id,
  })

  // ─── Mutations ────────────────────────────────────────────────────────────
  const calcMut = useMutation({
    mutationFn: () => hrApi.payroll.calculatePayslip(id!),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['payslip', id] })
      notify({ type: 'success', message: `Bulletin calculé. Net = ${fmtXOF(data.netPayable)}` })
    },
    onError: (err: any) => notify({ type: 'error', message: err?.response?.data?.message || 'Erreur calcul' }),
  })

  const pdfMut = useMutation({
    mutationFn: () => hrApi.payroll.generatePdf(id!),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['payslip', id] })
      setPdfUrl(data.presignedUrl)
      notify({ type: 'success', message: `PDF généré (${(data.pdfSize / 1024).toFixed(1)} KB)` })
    },
    onError: (err: any) => notify({ type: 'error', message: err?.response?.data?.message || 'Erreur PDF' }),
  })

  const sealMut = useMutation({
    mutationFn: () => hrApi.payroll.sealPdf(id!),
    onSuccess: (data) => {
      notify({
        type:    'success',
        message: data.alreadySealed ? 'Déjà scellé' : `Bulletin scellé WORM — ${data.wormStorageKey.slice(0, 40)}...`,
      })
    },
    onError: (err: any) => notify({ type: 'error', message: err?.response?.data?.message || 'Erreur scellage' }),
  })

  const deleteVarMut = useMutation({
    mutationFn: (varId: string) => hrApi.payroll.deleteVariable(varId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payslip-variables', id] })
      notify({ type: 'success', message: 'Variable supprimée' })
    },
  })

  if (isLoading) return <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" /></div>
  if (!payslip) return null

  const gains    = payslip.lines?.filter(l => !l.isDeduction && !l.code?.match(/^(IPRES_..._P|CSS_..._P|CFCE)$/)) ?? []
  const deducts  = payslip.lines?.filter(l => l.isDeduction)  ?? []
  const patron   = payslip.lines?.filter(l => !l.isDeduction && l.code?.match(/^(IPRES_..._P|CSS_..._P|CFCE)$/)) ?? []
  const isEditable  = payslip.status === 'DRAFT' || payslip.status === 'CALCULATED'
  const isCalculated = payslip.status !== 'DRAFT'
  const hasPdf       = !!payslip.pdfUrl

  return (
    <div className="space-y-5 nx-fade-up">
      {/* HEADER + ACTIONS */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
            <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
          </button>
          <h1 className="text-xl font-bold text-[var(--brand-dark)]">
            Bulletin — {payslip.employee?.firstName} {payslip.employee?.lastName}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowGrossUp(true)}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-[var(--bd)] hover:bg-[var(--surface-2)] text-sm font-medium text-[var(--tx2)] rounded-lg transition-colors"
          >
            <Calculator className="w-4 h-4" />
            Net → Brut
          </button>

          {isEditable && (
            <button
              onClick={() => calcMut.mutate()}
              disabled={calcMut.isPending}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--blue)] hover:bg-[#2DAFD9] text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {calcMut.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Calculer
            </button>
          )}

          {isCalculated && (
            <button
              onClick={() => pdfMut.mutate()}
              disabled={pdfMut.isPending}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--brand-amber)] hover:bg-[#4F3573] text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {pdfMut.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Générer PDF
            </button>
          )}

          {hasPdf && (
            <button
              onClick={() => sealMut.mutate()}
              disabled={sealMut.isPending}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--sb-bg)] hover:bg-[var(--brand-dark)] text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
              title="Scellage WORM legal-archive (10 ans)"
            >
              {sealMut.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Sceller
            </button>
          )}
        </div>
      </div>

      {/* HEADER BULLETIN */}
      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl p-6">
        <div className="flex justify-between mb-6 flex-wrap gap-4">
          <div>
            <p className="text-xs text-[var(--tx3)]">Employé</p>
            <p className="text-lg font-bold text-[var(--brand-dark)]">{payslip.employee?.firstName} {payslip.employee?.lastName}</p>
            <p className="text-sm text-[var(--tx3)]">{payslip.employee?.jobTitle}</p>
            <p className="text-xs text-[var(--tx3)] mt-1">{payslip.employee?.matricule}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-[var(--tx3)]">Période</p>
            <p className="text-lg font-bold text-[var(--brand-dark)]">
              {payslip.period && MONTHS_FR[payslip.period.month - 1]} {payslip.period?.year}
            </p>
            <p className="text-xs text-[var(--tx3)] mt-1">Statut : <span className="font-medium text-[var(--brand-dark)]">{payslip.status}</span></p>
          </div>
        </div>

        {/* GAINS */}
        {gains.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-2">Rémunérations</h4>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-[var(--bd)]">
                {gains.map(l => (
                  <tr key={l.id}>
                    <td className="py-1.5 text-[var(--tx2)]">{l.label}</td>
                    <td className="py-1.5 text-[var(--tx3)] text-xs">{l.base ? `Base: ${fmtXOF(l.base)}` : ''}</td>
                    <td className="py-1.5 text-right font-medium text-[var(--brand-dark)]">{fmtXOF(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* RETENUES */}
        {deducts.length > 0 && (
          <div className="mb-6">
            <h4 className="text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-2">Retenues</h4>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-[var(--bd)]">
                {deducts.map(l => (
                  <tr key={l.id}>
                    <td className="py-1.5 text-[var(--tx2)]">{l.label}</td>
                    <td className="py-1.5 text-[var(--tx3)] text-xs">{l.rate ? `${(toNum(l.rate) * 100).toFixed(1)}%` : ''}</td>
                    <td className="py-1.5 text-right font-medium text-[#DC2626]">−{fmtXOF(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* RÉSUMÉ */}
        <div className="border-t border-[var(--bd)] pt-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-[var(--tx3)]">Salaire brut</span>
            <span className="text-[var(--brand-dark)] font-medium">{fmtXOF(payslip.grossSalary)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[var(--tx3)]">Total retenues</span>
            <span className="text-[#DC2626] font-medium">−{fmtXOF(payslip.totalDeductions)}</span>
          </div>
          <div className="flex justify-between text-base font-bold pt-2 border-t border-[var(--bd)]">
            <span className="text-[var(--brand-dark)]">Net à payer</span>
            <span className="text-[#059669]">{fmtXOF(payslip.netPayable)}</span>
          </div>
        </div>
      </div>

      {/* VARIABLES PAIE */}
      <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h4 className="text-sm font-semibold text-[var(--brand-dark)]">Variables paie ponctuelles</h4>
            <p className="text-xs text-[var(--tx3)]">Heures sup, primes, retenues exceptionnelles, jours travaillés</p>
          </div>
          {isEditable && (
            <button
              onClick={() => setShowVarForm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--blue)] hover:bg-[#2DAFD9] text-xs font-medium text-white rounded-lg transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Ajouter
            </button>
          )}
        </div>

        {variables.length === 0 ? (
          <p className="text-sm text-[var(--tx3)] py-6 text-center">Aucune variable saisie pour ce bulletin.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)] text-xs text-[var(--tx3)] uppercase tracking-wider">
                <th className="text-left py-2 font-medium">Type</th>
                <th className="text-left py-2 font-medium">Libellé</th>
                <th className="text-left py-2 font-medium">Quantité</th>
                <th className="text-right py-2 font-medium">Montant</th>
                <th className="text-center py-2 font-medium">Imposable</th>
                {isEditable && <th className="text-right py-2 font-medium">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {variables.map(v => (
                <tr key={v.id}>
                  <td className="py-2 text-[var(--tx2)]">
                    <span className={`text-xs px-2 py-0.5 rounded ${v.isDeduction ? 'bg-[#FEF2F2] text-[#DC2626]' : 'bg-[#F0FDF4] text-[#059669]'}`}>
                      {v.type}
                    </span>
                  </td>
                  <td className="py-2 text-[var(--tx2)]">{v.label}</td>
                  <td className="py-2 text-[var(--tx3)] text-xs">{v.quantity ? `${v.quantity} × ${v.unitValue ? fmtXOF(v.unitValue) : '—'}` : '—'}</td>
                  <td className={`py-2 text-right font-medium ${v.isDeduction ? 'text-[#DC2626]' : 'text-[var(--brand-dark)]'}`}>
                    {v.isDeduction ? '−' : '+'}{fmtXOF(v.amount)}
                  </td>
                  <td className="py-2 text-center">
                    {v.isImposable ? (
                      <CheckCircle className="w-4 h-4 text-[#059669] inline" />
                    ) : (
                      <span className="text-xs text-[var(--tx3)]">non</span>
                    )}
                  </td>
                  {isEditable && (
                    <td className="py-2 text-right">
                      <button
                        onClick={() => deleteVarMut.mutate(v.id)}
                        className="p-1 hover:bg-[#FEF2F2] rounded transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-[#DC2626]" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* CHARGES PATRONALES */}
      {patron.length > 0 && (
        <div className="bg-[#FFF8EB] border border-[#F59E0B]/25 rounded-xl p-5">
          <h4 className="text-xs font-semibold text-[#D97706] uppercase tracking-wider mb-3">Charges patronales (à titre informatif)</h4>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-[#FCD9A4]">
              {patron.map(l => (
                <tr key={l.id}>
                  <td className="py-1.5 text-[#92400E]">{l.label}</td>
                  <td className="py-1.5 text-[#92400E] text-xs">{l.rate ? `${(toNum(l.rate) * 100).toFixed(2)}%` : ''}</td>
                  <td className="py-1.5 text-right font-medium text-[#92400E]">{fmtXOF(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-[#FCD9A4] mt-3 pt-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-[#92400E]">Total charges patronales</span>
              <span className="text-[#D97706] font-bold">{fmtXOF(payslip.employerCharges)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#92400E]">Coût total employeur</span>
              <span className="text-[var(--brand-dark)] font-bold">{fmtXOF(toNum(payslip.grossSalary) + toNum(payslip.employerCharges))}</span>
            </div>
          </div>
        </div>
      )}

      {/* PREVIEW PDF */}
      {pdfUrl && (
        <div className="bg-white border border-[var(--bd)] rounded-xl overflow-hidden shadow-sm">
          <div className="flex justify-between items-center p-4 border-b border-[var(--bd)]">
            <div className="flex items-center gap-2">
              <FileCheck className="w-4 h-4 text-[#059669]" />
              <h4 className="text-sm font-semibold text-[var(--brand-dark)]">Aperçu du PDF</h4>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener"
                className="text-xs text-[var(--blue)] hover:underline"
              >
                Ouvrir dans un nouvel onglet
              </a>
              <button onClick={() => setPdfUrl(null)} className="p-1 hover:bg-[var(--surface-2)] rounded">
                <X className="w-4 h-4 text-[var(--tx3)]" />
              </button>
            </div>
          </div>
          <iframe src={pdfUrl} className="w-full h-[800px]" title="Bulletin PDF" />
        </div>
      )}

      {/* MODALES */}
      {showVarForm && id && (
        <VariableFormModal
          payslipId={id}
          onClose={() => setShowVarForm(false)}
        />
      )}

      {showGrossUp && (
        <GrossUpModal onClose={() => setShowGrossUp(false)} />
      )}
    </div>
  )
}

// ============================================================================
//  MODAL — Saisie d'une variable paie
// ============================================================================

function VariableFormModal({ payslipId, onClose }: { payslipId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const notify = useNotificationStore(s => s.addNotification)

  const [type,        setType]        = useState<PayrollVariableType>('BONUS')
  const [code,        setCode]        = useState('')
  const [label,       setLabel]       = useState('')
  const [amount,      setAmount]      = useState('')
  const [quantity,    setQuantity]    = useState('')
  const [unitValue,   setUnitValue]   = useState('')
  const [isDeduction, setIsDeduction] = useState(false)
  const [isImposable, setIsImposable] = useState(true)

  const mut = useMutation({
    mutationFn: (dto: CreatePayrollVariableDto) => hrApi.payroll.createVariable(payslipId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payslip-variables', payslipId] })
      notify({ type: 'success', message: 'Variable ajoutée' })
      onClose()
    },
    onError: (err: any) => notify({ type: 'error', message: err?.response?.data?.message || 'Erreur' }),
  })

  // Préselection auto selon le type
  const VAR_PRESETS: Record<string, { code: string; label: string; deduction: boolean; imposable: boolean }> = {
    BONUS:             { code: 'BONUS',     label: 'Prime ponctuelle',           deduction: false, imposable: true  },
    BONUS_NON_TAXABLE: { code: 'BONUS_NI',  label: 'Prime non imposable',        deduction: false, imposable: false },
    OVERTIME_125:      { code: 'HSUP125',   label: 'Heures sup +25%',            deduction: false, imposable: true  },
    OVERTIME_150:      { code: 'HSUP150',   label: 'Heures sup +50%',            deduction: false, imposable: true  },
    OVERTIME_200:      { code: 'HSUP200',   label: 'Heures sup nuit/dimanche',   deduction: false, imposable: true  },
    DAYS_WORKED:       { code: 'JOURS_TR',  label: 'Jours travaillés',           deduction: false, imposable: false },
    DAYS_ABSENT_UNPAID:{ code: 'ABS_NPA',   label: 'Absences non payées',        deduction: false, imposable: false },
    ADVANCE_REPAYMENT: { code: 'AVANCE',    label: 'Remb. avance',                deduction: true,  imposable: false },
    LOAN_REPAYMENT:    { code: 'PRET',      label: 'Remb. prêt',                  deduction: true,  imposable: false },
    GARNISHMENT:       { code: 'SAISIE',    label: 'Saisie sur salaire',          deduction: true,  imposable: false },
    HEALTH_INSURANCE:  { code: 'MUTUELLE',  label: 'Mutuelle santé',              deduction: true,  imposable: false },
    DEDUCTION_OTHER:   { code: 'AUTRE_RET', label: 'Autre retenue',               deduction: true,  imposable: false },
  }

  const handleTypeChange = (newType: PayrollVariableType) => {
    setType(newType)
    const preset = VAR_PRESETS[newType]
    if (preset) {
      setCode(preset.code)
      setLabel(preset.label)
      setIsDeduction(preset.deduction)
      setIsImposable(preset.imposable)
    }
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const amt = Number(amount) || (Number(quantity) || 0) * (Number(unitValue) || 0)
    if (!code || !label || amt <= 0) {
      notify({ type: 'error', message: 'Code, label et montant > 0 requis' })
      return
    }
    mut.mutate({
      type,
      code,
      label,
      amount:      amt,
      quantity:    quantity   ? Number(quantity)   : undefined,
      unitValue:   unitValue  ? Number(unitValue)  : undefined,
      isImposable,
      isSubjectToIpres: isImposable,
      isSubjectToCss:   isImposable,
      isSubjectToCfce:  isImposable,
      isDeduction,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b border-[var(--bd)]">
          <h3 className="text-base font-semibold text-[var(--brand-dark)]">Nouvelle variable paie</h3>
          <button onClick={onClose} className="p-1 hover:bg-[var(--surface-2)] rounded">
            <X className="w-5 h-5 text-[var(--tx3)]" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Type</label>
            <select
              value={type}
              onChange={e => handleTypeChange(e.target.value as PayrollVariableType)}
              className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm"
            >
              <optgroup label="Gains">
                <option value="BONUS">Prime imposable</option>
                <option value="BONUS_NON_TAXABLE">Prime non imposable</option>
                <option value="OVERTIME_125">Heures sup +25%</option>
                <option value="OVERTIME_150">Heures sup +50%</option>
                <option value="OVERTIME_200">Heures sup nuit/dim.</option>
                <option value="COMMISSION">Commission</option>
                <option value="ALLOWANCE_OTHER">Autre indemnité</option>
              </optgroup>
              <optgroup label="Variables temps">
                <option value="DAYS_WORKED">Jours travaillés (prorata)</option>
                <option value="DAYS_ABSENT_UNPAID">Absences non payées</option>
                <option value="DAYS_SICK_PAID">Maladie payée</option>
                <option value="DAYS_LEAVE_PAID">Congés payés</option>
              </optgroup>
              <optgroup label="Retenues">
                <option value="ADVANCE_REPAYMENT">Remb. avance</option>
                <option value="LOAN_REPAYMENT">Remb. prêt</option>
                <option value="GARNISHMENT">Saisie sur salaire</option>
                <option value="UNION_DUES">Cotisation syndicale</option>
                <option value="HEALTH_INSURANCE">Mutuelle santé</option>
                <option value="DEDUCTION_OTHER">Autre retenue</option>
              </optgroup>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Code</label>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="HSUP125"
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Libellé</label>
              <input
                type="text"
                value={label}
                onChange={e => setLabel(e.target.value)}
                placeholder="Heures sup +25%"
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Quantité</label>
              <input
                type="number"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
                placeholder="10"
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Valeur unit.</label>
              <input
                type="number"
                value={unitValue}
                onChange={e => setUnitValue(e.target.value)}
                placeholder="3000"
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Montant total</label>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="30000"
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm font-semibold"
              />
            </div>
          </div>
          <p className="text-xs text-[var(--tx3)]">Si quantité × valeur unitaire saisis, le montant est calculé automatiquement.</p>

          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-[var(--tx2)]">
              <input
                type="checkbox"
                checked={isImposable}
                onChange={e => setIsImposable(e.target.checked)}
                className="w-4 h-4"
              />
              Imposable (IRPP + cotisations)
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--tx2)]">
              <input
                type="checkbox"
                checked={isDeduction}
                onChange={e => setIsDeduction(e.target.checked)}
                className="w-4 h-4"
              />
              Retenue (déduit du net)
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white border border-[var(--bd)] hover:bg-[var(--surface-2)] text-sm font-medium text-[var(--tx2)] rounded-lg"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={mut.isPending}
              className="flex-1 px-4 py-2 bg-[var(--blue)] hover:bg-[#2DAFD9] text-sm font-medium text-white rounded-lg disabled:opacity-50"
            >
              {mut.isPending ? 'Enregistrement...' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ============================================================================
//  MODAL — Calculateur Gross-Up (net cible → brut)
// ============================================================================

function GrossUpModal({ onClose }: { onClose: () => void }) {
  const notify = useNotificationStore(s => s.addNotification)

  const [netTarget,    setNetTarget]    = useState('')
  const [fiscalParts,  setFiscalParts]  = useState(1)
  const [isCadre,      setIsCadre]      = useState(false)
  const [transport,    setTransport]    = useState('26000')
  const [result,       setResult]       = useState<GrossUpResult | null>(null)

  const mut = useMutation({
    mutationFn: (dto: GrossUpDto) => hrApi.payroll.grossUp(dto),
    onSuccess: (data) => {
      setResult(data)
      notify({ type: 'success', message: `Brut trouvé en ${data.iterations} itérations` })
    },
    onError: (err: any) => notify({ type: 'error', message: err?.response?.data?.message || 'Erreur' }),
  })

  const compute = () => {
    if (!netTarget || Number(netTarget) <= 0) {
      notify({ type: 'error', message: 'Net cible requis' })
      return
    }
    const fixedComponents: any[] = []
    if (Number(transport) > 0) {
      fixedComponents.push({
        type:               'TRANSPORT_ALLOWANCE',
        code:               'TRANSP',
        label:              'Transport',
        amount:             Number(transport),
        isImposable:        false,
        isSubjectToIpres:   false,
        isSubjectToCss:     false,
        isSubjectToCfce:    false,
        subjectToProrata:   false,
      })
    }
    mut.mutate({
      netTarget:    Number(netTarget),
      fiscalParts,
      isCadre,
      daysPerMonth: 30,
      daysWorked:   30,
      fixedComponents,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b border-[var(--bd)]">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[var(--blue)]" />
            <h3 className="text-base font-semibold text-[var(--brand-dark)]">Calculateur Net → Brut</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--surface-2)] rounded">
            <X className="w-5 h-5 text-[var(--tx3)]" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-[var(--tx2)]">
            Trouve le <strong>salaire de base</strong> donnant le net cible souhaité après cotisations IPRES, IRPP, TRIMF.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Net à payer cible (FCFA)</label>
              <input
                type="number"
                value={netTarget}
                onChange={e => setNetTarget(e.target.value)}
                placeholder="300000"
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm font-semibold"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Parts fiscales</label>
              <select
                value={fiscalParts}
                onChange={e => setFiscalParts(Number(e.target.value))}
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm"
              >
                {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map(p => (
                  <option key={p} value={p}>{p} part{p > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Transport non imposable</label>
              <input
                type="number"
                value={transport}
                onChange={e => setTransport(e.target.value)}
                placeholder="26000"
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-[var(--tx2)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={isCadre}
                  onChange={e => setIsCadre(e.target.checked)}
                  className="w-4 h-4"
                />
                Cadre (IPRES Régime Compl.)
              </label>
            </div>
          </div>

          <button
            onClick={compute}
            disabled={mut.isPending}
            className="w-full px-4 py-2.5 bg-[var(--blue)] hover:bg-[#2DAFD9] text-sm font-medium text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {mut.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
            Calculer le brut
          </button>

          {result && (
            <div className="bg-[#F0FDF4] border border-[#10B981]/25 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center pb-2 border-b border-[#10B981]/25">
                <span className="text-xs font-semibold text-[#059669] uppercase tracking-wider">Résultat</span>
                <span className="text-xs text-[var(--tx3)]">{result.iterations} itérations</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--tx2)]">Salaire de base</span>
                <span className="font-bold text-[var(--brand-dark)]">{fmtXOF(result.baseSalary)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--tx2)]">Salaire brut total</span>
                <span className="font-medium text-[var(--brand-dark)]">{fmtXOF(result.grossSalary)}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-[#10B981]/25">
                <span className="text-[var(--tx2)]">Net effectif</span>
                <span className="font-bold text-[#059669]">{fmtXOF(result.netPayable)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--tx2)]">Coût employeur</span>
                <span className="font-medium text-[#D97706]">{fmtXOF(result.details.costForEmployer)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
